import {
  getLoginArguments,
  getTentativeLoginAccountId,
  LoginFnError,
  useLogin,
} from "apollo/mutations/login";
import {
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  TRACK_EVENTS,
} from "core/consts";
import { LOCAL_STORAGE_KEYS } from "core/model/localStorage";
import {
  getErrorMessage,
  isBackendErrorCode,
  isSealdIdentityRevokedErrorMessage,
} from "core/model/utils/errors";
import { safeJSONParse } from "core/model/utils/strings";
import {
  WIDGET_SCRIBE_FEATURE_ERROR,
  WIDGET_SCRIBE_ROLE_ERROR,
} from "core/model/utils/widget";
import {
  QueryProgress,
  RateLimitError,
  RateLimitErrorResponse,
} from "core/types";
import { millisecondsToMinutes } from "date-fns";
import { usePreventUnload } from "dsl/hooks";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import Translations from "translations/types";

export type LoginErrorState =
  | {
      challenge?: string;
      email?: string;
      general?: string;
      password?: string;
    }
  | undefined;

export type LoginView = "login" | "challenge";

const isRateLimitErrorResponse = (
  error: unknown,
): error is RateLimitErrorResponse =>
  (error as RateLimitErrorResponse)?.status === 429 &&
  !!(error as RateLimitErrorResponse).body?.expires_at &&
  !!(error as RateLimitErrorResponse).body?.remaining_time;

function handleRateLimitError(
  remainingTime: RateLimitError["remaining_time"],
  translations: Translations,
): LoginErrorState {
  return {
    general: translations.login.biometricfailedAlert.falsePasswordLimitReached({
      timer: String(millisecondsToMinutes(remainingTime) + 1),
    }),
  };
}

function handleChallengeError(translations: Translations): LoginErrorState {
  const datetimestring =
    window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.CHALLENGE_DATETIME) ?? "";
  return {
    challenge: translations.login.challengeScreen.verificationInvalidTimestamp({
      datetimestring,
    }),
  };
}

function handleSsoErrors(
  translations: Translations,
  errorCode?: number,
  isActivation?: boolean,
): LoginErrorState {
  if (isBackendErrorCode(errorCode, translations)) {
    const errorMessage = translations.general.errorCodes[errorCode];
    const activationMessage = isActivation
      ? translations.userManagement.ssoFlow.stepTwo.issueInfo
      : "";

    return {
      general: activationMessage
        ? `${activationMessage} ${errorMessage}`
        : errorMessage,
    };
  }

  return { general: translations.general.error };
}

function determineErrorByMessage(
  message: string,
  translations: Translations,
): LoginErrorState {
  if (message === WIDGET_SCRIBE_FEATURE_ERROR) {
    return { general: translations.login.widgetScribeFeatureError };
  }
  if (message === WIDGET_SCRIBE_ROLE_ERROR) {
    return { general: translations.login.widgetScribeRoleError };
  }
  if (message.includes("connected with unauthorized IP")) {
    return { general: "Please connect to the VPN before login" };
  }
  if (message.includes("no private key")) {
    return { general: translations.auctionResponse.accessDeniedError };
  }
  if (message === translations.login.toastInactiveProvider) {
    return { general: translations.login.toastInactiveProvider };
  }
  if (message === "cleandata in progress") {
    return { general: "cleandata in progress" };
  }
  if (isSealdIdentityRevokedErrorMessage(message)) {
    return { general: translations.login.sealdIdentityRevoked };
  }
  if (message.toLowerCase().includes("seald")) {
    console.error("[determineErrorByMessage]:", message);
    return { general: message };
  }
  if (message.toLowerCase().includes("no such user")) {
    return { general: translations.login.wrongEmailPassword };
  }
  if (message.toLowerCase().includes("bad password")) {
    return { general: translations.login.wrongEmailPassword };
  }
  console.error("[determineErrorByMessage]:", message);
  return { general: translations.general.error };
}

export function useHandleLogin({
  careproviderToLog,
}: {
  careproviderToLog?: number;
} = {}) {
  const [loginView, setLoginView] = useState<LoginView>("login");
  const login = useLogin();
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const [progressLogin, setProgressLogin] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [loginErrors, setErrors] = useState<LoginErrorState>();
  const isLogin = loginView === "login";
  const challengeTimestamp =
    window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.CHALLENGE_DATETIME) ?? "";
  const [search] = useSearchParams();
  const errorCode = search.get("errorType");
  const isActivation = search.get("isActivation");

  useEffect(() => {
    if (errorCode) {
      const error = handleSsoErrors(
        translations,
        Number(errorCode),
        isActivation === "true",
      );
      setErrors(error);
      if (isActivation === "true") {
        trackEvent({
          name: TRACK_EVENTS.SSO_ACTIVATION_FLOW_FAILED,
          reason: error,
        });
      } else {
        trackEvent({
          error_message: error,
          name: TRACK_EVENTS.SSO_LOGIN_FAILED,
          reason: error,
        });
      }
    }

    if (errorCode || isActivation) {
      const url = new URL(window.location.href);
      url.searchParams.delete("errorType");
      url.searchParams.delete("isActivation");
      window.history.replaceState({}, "", url.toString());
    }
  }, [
    errorCode,
    isActivation,
    setErrors,
    translations,
    trackEvent,
    careproviderToLog,
  ]);

  const resetError = useCallback(() => {
    setErrors(undefined);
  }, [setErrors]);

  const handleLogin = useCallback(
    async (
      {
        challenge,
        email,
        password,
        ssoToken,
      }: {
        challenge?: string;
        email?: string;
        password?: string;
        ssoToken?: string;
      },
      setProgress: Dispatch<SetStateAction<QueryProgress>>,
    ) => {
      resetError();
      setProgress(QUERY_PROGRESS_PENDING);
      try {
        const loginArgs = await getLoginArguments({
          careproviderToLog,
          challenge,
          password: password as string,
          token: ssoToken,
          username: email as string,
        });
        await login(loginArgs);
        setProgress(QUERY_PROGRESS_SUCCEED);

        if (ssoToken) {
          trackEvent({
            email,
            name: TRACK_EVENTS.SSO_LOGIN_SUCCESS,
          });
        }
      } catch (error) {
        if ((error as LoginFnError).needsChallenge) {
          setLoginView("challenge");
          setProgress(QUERY_PROGRESS_NOT_STARTED);
          return;
        }
        let errors: LoginErrorState;
        const message = getErrorMessage(error);

        const [, maybeRateLimitErrorResponse] = safeJSONParse(message);
        if (isRateLimitErrorResponse(maybeRateLimitErrorResponse)) {
          errors = handleRateLimitError(
            maybeRateLimitErrorResponse.body.remaining_time,
            translations,
          );
        } else if (challenge) {
          errors = handleChallengeError(translations);
        } else {
          errors = determineErrorByMessage(message, translations);
        }

        if (ssoToken) {
          trackEvent({
            email,
            error_message: message,
            name: TRACK_EVENTS.SSO_LOGIN_FAILED,
            reason: errors,
          });
        } else {
          trackEvent({
            account_id: getTentativeLoginAccountId(),
            email,
            error_message: message,
            name: TRACK_EVENTS.LOGIN_ATTEMPT_FAILED,
            reason: errors,
          });
        }

        setErrors(errors);
        setProgress(QUERY_PROGRESS_FAILED);
      }
    },
    [
      determineErrorByMessage,
      getErrorMessage,
      getLoginArguments,
      getTentativeLoginAccountId,
      handleChallengeError,
      handleRateLimitError,
      handleSsoErrors,
      isRateLimitErrorResponse,
      login,
      setErrors,
      setLoginView,
      trackEvent,
      translations,
    ],
  );

  usePreventUnload(progressLogin);

  return {
    challengeTimestamp,
    handleLogin,
    isLogin,
    loginErrors,
    loginView,
    progressLogin,
    resetError,
    setLoginView,
    setProgressLogin,
  };
}
